import { Formik } from "formik";

import { InfoRounded } from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { Box, Button, colors, Drawer, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

import AutocompleteFilter from "@components/Forms/FormInputs/AutocompleteFilter";
import CheckBox from "@components/Forms/FormInputs/CheckBox";

import { ICompanyTableControls } from "@store/filters/filter.types";
import { useTableStore } from "@store/filters/filters.store";
import { PopupActions } from "@store/popupStore";

import AssociationSelect from "./AssociationSelect";
import CompanyTypeSelect from "./CompanyTypeSelect";
import EmployeeSizeSelect from "./EmployeeSizeSelect";
import FilterFieldLabel from "./FilterFieldLabel";
import FilterStyles from "./FilterStyles";
import { maxEmployeesToNumeric, minEmployeesToNumeric } from "./utilities/Company";

interface ICompanyFiltersDrawerProperties {
  filterKey?: "company" | "technologyCompany";
  isOpen: boolean;
  setOpen: (newState: boolean) => void;
}

export default function CompanyFiltersDrawer({
  filterKey = "company",
  isOpen,
  setOpen,
}: ICompanyFiltersDrawerProperties) {
  const { filters, resetTableStore, setTableFilters } = useTableStore(filterKey);

  const handleFilterChange = (
    values: Omit<ICompanyTableControls["filters"], "pageSize" | "sort">,
    newKeywords?: string[],
    newLocations?: string[],
    newIndustries?: string[],
  ) => {
    const preppedValues = {
      ...values,
      industries: newIndustries ?? filters.industries,
      keywords: newKeywords ?? filters.keywords,
      locations: newLocations ?? filters.locations,
      maxEmployees: maxEmployeesToNumeric(values.maxEmployees ?? filters.maxEmployees),
      minEmployees: minEmployeesToNumeric(values.minEmployees ?? filters.minEmployees),
    };

    PopupActions.openAlert({
      duration: 1000,
      message: "Filter Applied",
      severity: "success",
    });

    setTableFilters(preppedValues);
  };

  return (
    <Drawer
      anchor="top"
      onClose={() => setOpen(false)}
      open={isOpen}
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.70)",
          },
        },
      }}
      sx={{
        ".MuiBox-root": {
          backgroundColor: colors.grey[50],
        },
        ".MuiDrawer-paper": { overflowY: "auto" },
      }}
      transitionDuration={300}
    >
      <Formik
        enableReinitialize
        initialValues={{
          autm2023: filters.autm2023,
          autm2024: filters.autm2024,
          bio2022: filters.bio2022,
          bio2023: filters.bio2023,

          govLicense: filters.govLicense,

          keywords: filters.keywords,
          les2023: filters.les2023,

          licensed: filters.licensed,

          sbir: filters.sbir,

          sponsored: filters.sponsored,
          uidp2023: filters.uidp2023,
        }}
        onSubmit={() => void 0}
      >
        {({ values }) => (
          <Box sx={FilterStyles.drawer}>
            <Box sx={FilterStyles.headerContainer}>
              <Typography variant="h6">Company Filters</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { md: "row", xs: "column" },
                  gap: 3,
                }}
              >
                <Box flex={1}>
                  <FilterFieldLabel
                    label="Location"
                    subLabel="Filter by company HQ location: country, state, city, or region."
                  />
                  <AutocompleteFilter
                    color="secondary"
                    isFullWidth
                    isMultiple
                    label="E.g. United States, Colorado, Denver, Etc."
                    limitTags={2}
                    name="locations"
                    onChange={(newLocationsArray) => {
                      handleFilterChange(values, filters.keywords, newLocationsArray, filters.industries);
                    }}
                    size="small"
                    type="text"
                    value={filters.locations ?? []}
                  />
                </Box>
                <Box flex={1}>
                  <FilterFieldLabel
                    label="Industry"
                    subLabel="Segment by industry verticals like biotech, defense, or medtech."
                  />
                  <AutocompleteFilter
                    color="secondary"
                    isFullWidth
                    isMultiple
                    label="E.g. Biotech, Semiconductors, Aerospace, Medical Devices"
                    limitTags={2}
                    name="industries"
                    onChange={(newIndustriesArray) => {
                      handleFilterChange(values, filters.keywords, filters.locations, newIndustriesArray);
                    }}
                    size="small"
                    type="text"
                    value={filters.industries}
                  />
                </Box>
                <Box flex={1}>
                  <FilterFieldLabel
                    label="Keywords"
                    subLabel="Search terms from company descriptions, not technology keywords."
                  />
                  <AutocompleteFilter
                    color="secondary"
                    isFullWidth
                    isMultiple
                    label="E.g. AI drug discovery, quantum sensing, synthetic biology"
                    limitTags={2}
                    name="keywords"
                    onChange={(newKeywordsArray) => {
                      handleFilterChange(values, newKeywordsArray, filters.locations, filters.industries);
                    }}
                    size="small"
                    type="text"
                    value={filters.keywords}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { md: "row", xs: "column" },
                  gap: 3,
                }}
              >
                <Box flex={1}>
                  <FilterFieldLabel label="Company Size" subLabel="Filter by total number employees at a company." />
                  <EmployeeSizeSelect onChange={setTableFilters} value={filters.companyEmployeeSizes!} />
                </Box>
                <Box flex={1}>
                  <FilterFieldLabel
                    label="Company Type"
                    subLabel="Select companies that are either non-profit or for-profit."
                  />
                  <CompanyTypeSelect onChange={setTableFilters} value={filters.companyTypes!} />
                </Box>
                <Box flex={1}>
                  <FilterFieldLabel
                    label="Associations"
                    subLabel="Limit to groups like AUTM, LES, BIO, or other licensing orgs."
                  />
                  <AssociationSelect
                    filterState={filters}
                    onChange={(updatedValues) => {
                      handleFilterChange(updatedValues, filters.keywords, filters.locations, filters.industries);
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h6">Data Filters</Typography>
                <Box
                  sx={{
                    alignItems: { md: "center", xs: "flex-start" },
                    display: "flex",
                    flexDirection: { md: "row", xs: "column" },
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { md: "row", xs: "column" },
                      gap: 3,
                    }}
                  >
                    <Box>
                      <CheckBox
                        color="secondary"
                        label={
                          <Typography sx={FilterStyles.checkboxLabel}>
                            <Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "center" }}>
                              <Typography sx={FilterStyles.checkboxLabel}>Licensed IP</Typography>
                              <Tooltip title="See companies who have licensed technologies.">
                                <InfoRounded color="info" sx={{ cursor: "help", fontSize: "1.25rem" }} />
                              </Tooltip>
                            </Box>
                          </Typography>
                        }
                        name="licensed"
                        onChange={(newCheckedState) =>
                          handleFilterChange(
                            {
                              ...values,
                              licensed: newCheckedState,
                            },
                            filters.keywords,
                            filters.locations,
                            filters.industries,
                          )
                        }
                        size="small"
                      />
                    </Box>
                    <Box>
                      <CheckBox
                        color="secondary"
                        label={
                          <Typography sx={FilterStyles.checkboxLabel}>
                            <Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "center" }}>
                              <Typography sx={FilterStyles.checkboxLabel}>Partnerships</Typography>
                              <Tooltip
                                sx={{ cursor: "help" }}
                                title="See companies that teamed up with Universities to Sponsor research."
                              >
                                <InfoRounded color="info" sx={{ cursor: "help", fontSize: "1.25rem" }} />
                              </Tooltip>
                            </Box>
                          </Typography>
                        }
                        name="sponsored"
                        onChange={(newCheckedState) => {
                          handleFilterChange(
                            {
                              ...values,
                              sponsored: newCheckedState,
                            },
                            filters.keywords,
                            filters.locations,
                            filters.industries,
                          );
                        }}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <CheckBox
                        color="secondary"
                        label={
                          <Typography sx={FilterStyles.checkboxLabel}>
                            <Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "center" }}>
                              <Typography sx={FilterStyles.checkboxLabel}>Grant Awardees</Typography>
                              <Tooltip sx={{ cursor: "help" }} title="See companies awarded SBIR or STTR grants.">
                                <InfoRounded color="info" sx={{ cursor: "help", fontSize: "1.25rem" }} />
                              </Tooltip>
                            </Box>
                          </Typography>
                        }
                        name="sbir"
                        onChange={(newCheckedState) =>
                          handleFilterChange(
                            { ...values, sbir: newCheckedState },
                            filters.keywords,
                            filters.locations,
                            filters.industries,
                          )
                        }
                        size="small"
                      />
                    </Box>
                    <Box>
                      <CheckBox
                        color="secondary"
                        label={
                          <Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "center" }}>
                            <Typography sx={FilterStyles.checkboxLabel}>Gov. Licensing</Typography>
                            <Tooltip
                              sx={{ cursor: "help" }}
                              title="See companies that have entered into licensing agreements with the US government."
                            >
                              <InfoRounded color="info" sx={{ cursor: "help", fontSize: "1.25rem" }} />
                            </Tooltip>
                          </Box>
                        }
                        name="govLicense"
                        onChange={(newCheckedState) =>
                          handleFilterChange(
                            { ...values, govLicense: newCheckedState },
                            filters.keywords,
                            filters.locations,
                            filters.industries,
                          )
                        }
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      mt: { md: 0, xs: 2 },
                      width: "100%",
                    }}
                  >
                    <Button
                      color="warning"
                      onClick={() => resetTableStore()}
                      startIcon={<RestartAltRoundedIcon />}
                      sx={{ borderRadius: "8px", color: "white" }}
                      variant="contained"
                    >
                      Reset Filters
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() => setOpen(false)}
                      startIcon={<CloseRoundedIcon />}
                      sx={{ borderRadius: "8px", color: "white" }}
                      variant="contained"
                    >
                      Close
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Formik>
    </Drawer>
  );
}
